<script setup lang="ts">
import { getSeoDesc } from '@/utils/getSeoDescription'

import { REQUEST_STATUS } from '@/constants/api/requestStatus'

import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'

import { CategoryPage as CategoryPageType } from '@/types/api/api-v4/category'
import { AsyncDataKey } from '@/types/main'

import AppSeoDescription from '@/components/App/AppSeoDescription.vue'
import CategoryEmpty from '@/components/Category/CategoryEmpty.vue'
import CategoryPage from '@/components/Category/CategoryPage.vue'
import SkeletonCategoryPage from '@/components/Skeleton/SkeletonCategoryPage.vue'

import { NavigateToOptions } from '#app/composables/router'

const { $apiService, $metrics } = useNuxtApp()

const CITY_FOR_PIZZA_REDIRECT = 'chel'
const PIZZA_CATEGORY_SLUG = 'pizza'
const ROME_PIZZA_CATEGORY_SLUG = '/category/rpizza/'

const { isDesktop } = useResponsive()

const route = useRoute()

const cityStore = useCityStore()
const { menuByPointsEnabled } = useToggleStore()

const { domain } = cityStore.cityInfo

const {
  menuByPointData,
  isAddressV5Selected,
  getAsyncDataKeyComputed,
  getAvailableProductsData,
  syncAddressWithV5Api,
} = useMenuByPoint()

// Ключ для fetch продуктов категории на основе выбранного адреса
const categoryProductsKey = getAsyncDataKeyComputed(
  `${AsyncDataKey.ONE_CATEGORY_PRODUCTS_DATA}-${route.params.category}`,
)

// Функция для фича-тагла menuByPointsEnabled. если отключено, фетч продуктов не будет происходить
const oneCategoryProductsV5 = async () => {
  if (!menuByPointsEnabled) {
    return {
      data: ref(null),
      error: ref(null),
      status: ref(REQUEST_STATUS.success),
    }
  }

  return useFetchData(
    async () => {
      const slug = String(route.params.category)

      const result = await fetchHandler(() =>
        $apiService.category.fetchOneCategoryProductsV5(
          slug,
          menuByPointData.value,
        ),
      )

      // Если нет метода доставки или адреса, то возвращаем то что прислал бэк
      if (!result || !isAddressV5Selected.value) return result

      // Из списка продуктов убираем недоступные
      result.products = result.products.filter((product) => {
        const availableProductsData = getAvailableProductsData(product)

        if (!availableProductsData.isAvailable) return false

        // Заменяем список модификаций продукта только доступными модификациями
        product.modifications = availableProductsData.modifications

        return true
      })

      return result
    },
    {
      withoutCache: true,
      lazy: true,
      key: categoryProductsKey.value,
      watch: [categoryProductsKey],
    },
  )
}

if (import.meta.client) {
  await callOnce(async () => {
    if (!menuByPointsEnabled) return

    // Синхронизируем адрес с V5 API меню
    await syncAddressWithV5Api()
  })
}

const {
  data: categoryProducts,
  error: errorCategoryProducts,
  status: statusCategoryProducts,
} = await oneCategoryProductsV5()

watch(
  () => errorCategoryProducts.value,
  () => {
    if (errorCategoryProducts.value) showError(errorCategoryProducts.value)
  },
)

const {
  data: dataCategory,
  error: errorCategory,
  status: statusCategory,
} = await useFetchData(
  () => {
    const slug = String(route.params.category)

    return menuByPointsEnabled
      ? fetchHandler(() => $apiService.category.fetchOneCategoryV5(slug))
      : fetchHandler(() => $apiService.category.fetchOneCategory(slug))
  },
  {
    withoutCache: true,
    key: `${AsyncDataKey.ONE_CATEGORY_DATA}-${route.params.category}`,
    lazy: true,
  },
)

watch(
  () => errorCategory.value,
  () => {
    if (errorCategory.value) showError(errorCategory.value)
  },
)

const currentCategory = computed(() => {
  if (!menuByPointsEnabled) {
    return dataCategory.value as CategoryPageType
  }

  const products = categoryProducts.value ?? {
    products: [],
    tags: [],
  }

  return {
    breadcrumbs: dataCategory.value?.breadcrumbs ?? [],
    description: dataCategory.value?.description ?? '',
    factoids: dataCategory.value?.factoids ?? [],
    name: dataCategory.value?.name ?? '',
    seo: dataCategory.value?.seo ?? null,
    slug: dataCategory.value?.slug ?? '',
    ...products,
  }
})

const emptyPageShow = computed(() => {
  return currentCategory.value.products?.length === 0
})

const isFetchDataLoading = computed(() => {
  return statusCategory.value !== REQUEST_STATUS.success
})

const showSkeletonLoader = computed(() => {
  const categoryLoading = REQUEST_STATUS.success !== statusCategory.value

  const categoryProductsLoading =
    REQUEST_STATUS.success !== statusCategoryProducts.value

  return categoryLoading || categoryProductsLoading
})

const seoDesc = computed(
  () =>
    currentCategory.value?.seo?.seo_text ||
    getSeoDesc(
      route.params?.category,
      cityStore.cityInfo?.padeg?.pred || '',
      cityStore.cityInfo.phone,
    ),
)

definePageMeta({
  middleware: (to) => {
    const { isLandingPageEnabled } = useToggleStore()

    if (isLandingPageEnabled) return navigateTo('/')

    const { cityInfo } = useCityStore()

    /* Редирект для категории rpizza для челябинска */
    if (
      cityInfo.domain_prefix === CITY_FOR_PIZZA_REDIRECT &&
      to.params.category === PIZZA_CATEGORY_SLUG
    ) {
      const options: NavigateToOptions = {
        redirectCode: 301,
      }

      if (import.meta.server) options.external = true

      return navigateTo(ROME_PIZZA_CATEGORY_SLUG, options)
    }
  },
})

usePopupFetch()

onMounted(() => {
  // Открытие категории
  $metrics?.eventSendGoal({ ym: 'category_opened' })

  watchEffect(() => {
    // Меню успешно загрузилось
    if (categoryProducts.value) $metrics?.eventSendGoal({ ym: 'menu_loaded' })
  })
})
</script>

<template>
  <div
    class="category-page"
    :class="{ 'category-page--loading': isFetchDataLoading }"
  >
    <skeleton-category-page v-if="showSkeletonLoader" />

    <app-head
      :title="currentCategory?.seo?.seo_title"
      :seo="{
        title: currentCategory?.seo?.seo_title,
        description: currentCategory?.seo?.seo_description,
        keywords: currentCategory?.seo?.seo_keywords,
      }"
    />

    <template v-if="!showSkeletonLoader">
      <the-breadcrumbs
        v-if="currentCategory?.breadcrumbs"
        class="category-page__breadcrumbs"
        :breadcrumbs="currentCategory.breadcrumbs"
        :domain="domain"
      />

      <h1 v-if="currentCategory?.name" class="category-page__headline">
        {{ currentCategory?.seo?.seo_h1 }}
      </h1>

      <div
        v-if="isDesktop && currentCategory?.description"
        class="category-page__description"
        v-html="currentCategory?.description"
      />

      <category-empty v-if="emptyPageShow" />

      <category-page
        v-else-if="currentCategory"
        :current-category="currentCategory"
        :is-loading="isFetchDataLoading"
      />

      <!--
      Здесь условие на pending затем, чтобы перерисовка
      между состояниями (смены категорий) не была такая заметная
    -->
      <app-seo-description
        v-if="seoDesc && !isFetchDataLoading"
        class="category-page__seo"
        :seo-content="seoDesc"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.category-page {
  &--loading {
    pointer-events: none;
    transition: all 3s cubic-bezier(0.075, 0.82, 0.165, 1);
  }

  @include media('<tablet') {
    padding: 20px 10px 30px;
  }

  &__breadcrumbs {
    padding-bottom: 20px;
  }

  &__headline {
    margin-bottom: 30px;
    font-size: 28px;
    font-weight: 900;

    @include media('<=tablet') {
      margin-bottom: 0;
      font-size: 18px;
      line-height: 21px;
    }
  }

  &__description {
    margin: 30px 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: var(--ui-text-primary);
  }

  &__seo {
    margin-top: 100px;

    @include media('<desktop-mini') {
      margin-top: 40px;
      margin-bottom: 0;
    }
  }
}
</style>
